import {
  FillStructuredTableUsingAIMatchResultResponse,
  FillStructuredTableUsingAIResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import sortedBy from 'util/sortedBy';

export enum MismatchErrorType {
  CELL_MISMATCH = 'CELL_MISMATCH',
  ROW_MISMATCH = 'ROW_MISMATCH',
  USER_EDIT = 'USER_EDIT',
}

/**
 * Error type specific to cross-model validation for displaying mismatches.
 */
export interface SimpleCellError {
  /**
   * Type of mismatch error (cell-level or row-level).
   */
  type: MismatchErrorType;
  /**
   * Message describing the mismatch.
   */
  message: string;
}

/**
 * A simplified representation of a cell for cross-model validation.
 */
export interface SimpleCellData {
  /**
   * The original row index of the cell (for filtering).
   */
  originalRowIndex: number;
  /**
   * The primary text content of the cell.
   */
  primaryText: string;
  /**
   * The secondary text content of the cell (for mismatch cases).
   */
  secondaryText: string | null;
  /**
   * The manually entered text content of the cell.
   */
  userText: string | null;
  /**
   * Optional error information if the cell has validation issues.
   */
  error: SimpleCellError | null;
}

/**
 * Diff-like information to show to the user in cross-model validation.
 */
export interface CrossModelComparisonTables {
  /**
   * Primary model results that will be shown to the user as the rows that will be inserted.
   */
  primaryTable: Array<Array<SimpleCellData>>;
  /**
   * Secondary tables showing extra rows that *won't* be inserted but were produced by the non-primary
   * models.
   */
  secondaryExtraTables: Array<{
    displayName: string;
    table: Array<Array<SimpleCellData>>;
  }>;
}

export function getComparisonCellDisplayText(cell: SimpleCellData): string {
  if (cell.userText != null) {
    return cell.userText;
  }
  if (cell.secondaryText != null) {
    return `${cell.primaryText} | ${cell.secondaryText}`;
  }
  return cell.primaryText;
}

function mismatchError(message: string, type: MismatchErrorType): SimpleCellError {
  return {type, message};
}

export function isCrossModelValidationVerified(fillResult: FillStructuredTableUsingAIResponse): boolean {
  return fillResult.pairwiseComparisons[0]?.isExactMatch ?? false;
}

/**
 * Render the table cells of a diff view allowing the possibility of a mismatch.
 *
 * In addition to returning the table to show to the user, we include any extra rows from other
 * runs that didn't cleanly match the table.
 */
export function generateCrossModelComparisonTables(
  fillResult: FillStructuredTableUsingAIResponse,
  primaryIndex: number = 0
): CrossModelComparisonTables {
  const primaryDisplayName = fillResult.runResults[primaryIndex].displayName;
  const secondaryDisplayName = fillResult.runResults[primaryIndex === 0 ? 1 : 0].displayName;
  const pairwiseComparison = fillResult.pairwiseComparisons[0] ?? null;
  if (pairwiseComparison == null) {
    return {
      primaryTable: fillResult.runResults[0].newRows.map((row, rowNumber) =>
        row.map(
          (cell): SimpleCellData => ({
            originalRowIndex: rowNumber,
            primaryText: cell.text,
            secondaryText: null,
            userText: null,
            error: mismatchError(
              `Model ${fillResult.runResults[1].displayName} failed and returned no response.`,
              MismatchErrorType.ROW_MISMATCH
            ),
          })
        )
      ),
      secondaryExtraTables: [],
    };
  }

  const primaryMatchResults = sortedBy(
    pairwiseComparison.matchResults.filter((matchResult) =>
      primaryIndex === 0 ? matchResult.row1 != null : matchResult.row2 != null
    ),
    [{accessor: (matchResult) => (primaryIndex === 0 ? matchResult.row1!.index : matchResult.row2!.index)}]
  );

  const primaryTable = primaryMatchResults.map((matchResult, rowNumber) => {
    const primaryRow = primaryIndex === 0 ? matchResult.row1! : matchResult.row2!;
    const secondaryRow = primaryIndex === 0 ? matchResult.row2 : matchResult.row1;
    return generateComparisonRow(
      {
        ...matchResult,
        row1: primaryRow,
        row2: secondaryRow,
      },
      primaryDisplayName,
      secondaryDisplayName,
      rowNumber
    );
  });

  const leftoverMatchResults = sortedBy(
    pairwiseComparison.matchResults.filter((matchResult) =>
      primaryIndex === 0 ? matchResult.row1 == null : matchResult.row2 == null
    ),
    [{accessor: (matchResult) => (primaryIndex === 0 ? matchResult.row2!.index : matchResult.row1!.index)}]
  );

  if (leftoverMatchResults.length === 0) {
    return {primaryTable, secondaryExtraTables: []};
  } else {
    return {
      primaryTable,
      secondaryExtraTables: [
        {
          displayName: secondaryDisplayName,
          table: leftoverMatchResults.map((matchResult, rowNumber) => {
            const row = primaryIndex === 0 ? matchResult.row2! : matchResult.row1!;
            return row.row.map((cell) => ({
              originalRowIndex: rowNumber,
              primaryText: cell.text,
              secondaryText: null,
              userText: null,
              error: mismatchError(
                `Model ${primaryDisplayName} did not produce a corresponding row.`,
                MismatchErrorType.ROW_MISMATCH
              ),
            }));
          }),
        },
      ],
    };
  }
}

function generateComparisonRow(
  matchResult: FillStructuredTableUsingAIMatchResultResponse,
  primaryDisplayName: string,
  secondaryDisplayName: string,
  rowNumber: number
): Array<SimpleCellData> {
  const row1 = matchResult.row1!;
  const row2 = matchResult.row2;
  if (row2) {
    return row1.row.map((cell1, i): SimpleCellData => {
      const cell2 = row2.row[i];
      if (cell1.text === cell2.text) {
        return {
          primaryText: cell1.text,
          originalRowIndex: rowNumber,
          secondaryText: null,
          userText: null,
          error: null,
        };
      } else {
        return {
          primaryText: cell1.text,
          secondaryText: cell2.text,
          originalRowIndex: rowNumber,
          userText: null,
          error: mismatchError(
            `${primaryDisplayName}: ${cell1.text} | ${secondaryDisplayName}: ${cell2.text}`,
            MismatchErrorType.CELL_MISMATCH
          ),
        };
      }
    });
  } else {
    return row1.row.map(
      (cell): SimpleCellData => ({
        primaryText: cell.text,
        originalRowIndex: rowNumber,
        secondaryText: null,
        userText: null,
        error: mismatchError(
          `Model ${secondaryDisplayName} did not produce a corresponding row.`,
          MismatchErrorType.ROW_MISMATCH
        ),
      })
    );
  }
}
